// src/components/catalog/CatalogSyncModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import Modal from '../common/Modal';
import LoadingSpinner from '../common/LoadingSpinner';
import {
    SYNC_CATALOGS,
    syncCatalog,
    readApiError,
    formatDataAmount,
    formatMoney,
} from '../../services/catalogApi';
import styles from './CatalogSyncModal.module.css';

// Rapor listelerinin eleman şekli:
//   bundles*   : { providerRef, title, providerType, scope, reason, bundleId }
//   plansInserted: { providerRef, bundleTitle, title, isUnlimited, dataMb,
//                    validityDays, providerPriceUsd, planId }
// reason / bundleId / planId null olabilir (dryRun'da planId hep null).

const bundleKey = (item, index) => `${item.providerRef || 'x'}-${item.title || index}`;

const BundleList = ({ items, tone }) => (
    <ul className={styles.bundleList}>
        {items.map((item, index) => (
            <li
                key={bundleKey(item, index)}
                className={`${styles.bundleRow} ${tone === 'reject' ? styles.bundleRowReject : ''}`}
            >
                <span className={styles.bundleName}>{item.title || '(başlıksız)'}</span>
                {item.scope && <span className={styles.chip}>{item.scope}</span>}
                {item.providerType && item.providerType !== item.scope && (
                    <span className={styles.chipMuted}>{item.providerType}</span>
                )}
                <span className={`${styles.bundleRef} num`}>#{item.providerRef ?? '—'}</span>
                {item.bundleId != null && (
                    <span className={`${styles.bundleRef} num`}>id:{item.bundleId}</span>
                )}
                {item.reason && <span className={styles.bundleReason}>{item.reason}</span>}
            </li>
        ))}
    </ul>
);

const CatalogSyncModal = ({ isOpen, onClose, onApplied }) => {
    const [catalog, setCatalog] = useState('unlimited');
    const [plan, setPlan] = useState(null);       // dryRun: true yanıtı
    const [applied, setApplied] = useState(null); // dryRun: false yanıtı
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [isApplying, setIsApplying] = useState(false);
    const [error, setError] = useState(null);

    // Modal her açılışta temiz başlar.
    useEffect(() => {
        if (!isOpen) return;
        setCatalog('unlimited');
        setPlan(null);
        setApplied(null);
        setError(null);
    }, [isOpen]);

    const handlePreview = useCallback(async (target) => {
        setIsPreviewing(true);
        setError(null);
        setPlan(null);
        setApplied(null);
        try {
            // dryRun: true — hiçbir satır yazılmaz.
            const data = await syncCatalog(target, { dryRun: true });
            setPlan(data);
        } catch (err) {
            console.error(`Katalog sync önizlemesi alınamadı (${target}):`, err);
            setError(readApiError(err, 'Önizleme alınamadı. Lütfen tekrar deneyin.').message);
        } finally {
            setIsPreviewing(false);
        }
    }, []);

    const handleApply = async () => {
        setIsApplying(true);
        setError(null);
        try {
            // KRİTİK: dryRun açıkça false gönderilmezse sunucu yine dry run yapar.
            const data = await syncCatalog(catalog, { dryRun: false });
            if (data?.dryRun) {
                setError('Sunucu isteği dry run olarak işledi; hiçbir satır yazılmadı. Tekrar deneyin.');
                setPlan(data);
                return;
            }
            setApplied(data);
            const inserted = (data?.bundlesInserted || []).length;
            toast.success(
                inserted > 0
                    ? `${inserted} paket eklendi. Fiyat girilene kadar yayına kapalı.`
                    : 'Sync tamamlandı; eklenecek yeni paket yoktu.'
            );
            if (onApplied) onApplied();
        } catch (err) {
            console.error(`Katalog sync uygulanamadı (${catalog}):`, err);
            setError(readApiError(err, 'Sync başarısız oldu. Hiçbir satır yazılmamıştır.').message);
        } finally {
            setIsApplying(false);
        }
    };

    const renderReport = (data, isApplyResult) => {
        const bundlesInserted = data.bundlesInserted || [];
        const bundlesSkipped = data.bundlesSkipped || [];
        const bundlesRejected = data.bundlesRejected || [];
        const plansInserted = data.plansInserted || [];
        const plansSkipped = data.plansSkipped || [];
        const countriesCreated = data.countriesCreated || [];

        return (
            <>
                <div className={styles.summaryRow}>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Sağlayıcıda</span>
                        <span className={`${styles.summaryValue} num`}>{data.providerBundleCount ?? '—'}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>{isApplyResult ? 'Eklenen paket' : 'Eklenecek paket'}</span>
                        <span className={`${styles.summaryValue} num`}>+{bundlesInserted.length}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>{isApplyResult ? 'Eklenen plan' : 'Eklenecek plan'}</span>
                        <span className={`${styles.summaryValue} num`}>+{plansInserted.length}</span>
                    </div>
                    <div className={styles.summaryItem}>
                        <span className={styles.summaryLabel}>Reddedilen</span>
                        <span className={`${styles.summaryValue} num`}>{bundlesRejected.length}</span>
                    </div>
                </div>

                <div className={styles.metaRow}>
                    <span>Yeni ülke: <strong className="num">{countriesCreated.length}</strong></span>
                    <span>Coverage satırı: <strong className="num">{data.coverageRowsInserted ?? 0}</strong></span>
                    <span>Bölge satırı: <strong className="num">{data.regionRowsInserted ?? 0}</strong></span>
                </div>

                {/* Reddedilenler ilk sırada: sessiz atlama olmaması tasarımın parçası. */}
                {bundlesRejected.length > 0 && (
                    <section className={styles.section}>
                        <h4 className={styles.sectionTitle}>
                            Reddedilenler
                            <span className={`${styles.sectionCount} num`}>{bundlesRejected.length}</span>
                        </h4>
                        <p className={styles.emptyNote}>
                            Bilerek alınmadı. Eski şemada zaten var olan ürünler ileride ID'leri
                            korunarak ayrı bir taşıma adımıyla geçecek.
                        </p>
                        <BundleList items={bundlesRejected} tone="reject" />
                    </section>
                )}

                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        {isApplyResult ? 'Eklenen paketler' : 'Eklenecek paketler'}
                        <span className={`${styles.sectionCount} num`}>{bundlesInserted.length}</span>
                    </h4>
                    {bundlesInserted.length > 0
                        ? <BundleList items={bundlesInserted} />
                        : <p className={styles.emptyNote}>Yeni paket yok.</p>}
                </section>

                <section className={styles.section}>
                    <h4 className={styles.sectionTitle}>
                        {isApplyResult ? 'Eklenen planlar' : 'Eklenecek planlar'}
                        <span className={`${styles.sectionCount} num`}>{plansInserted.length}</span>
                    </h4>
                    {plansInserted.length > 0 ? (
                        <div className={styles.tableWrapper}>
                            <table className={styles.planTable}>
                                <thead>
                                    <tr>
                                        <th>Paket</th>
                                        <th>Plan</th>
                                        <th>Veri</th>
                                        <th>Gün</th>
                                        <th>Maliyet (USD)</th>
                                        <th>Plan ID</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {plansInserted.map((item, index) => (
                                        <tr key={item.planId || `${item.providerRef}-${item.title}-${index}`}>
                                            <td>{item.bundleTitle}</td>
                                            <td>
                                                {item.title}
                                                {item.isUnlimited && <span className={styles.unlimitedTag}>sınırsız</span>}
                                            </td>
                                            <td className="num">{formatDataAmount(item)}</td>
                                            <td className="num">{item.validityDays ?? '—'}</td>
                                            <td className="num">{formatMoney(item.providerPriceUsd)}</td>
                                            <td className="num">{item.planId ?? '—'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <p className={styles.emptyNote}>Yeni plan yok.</p>
                    )}
                </section>

                {(bundlesSkipped.length > 0 || plansSkipped.length > 0) && (
                    <section className={styles.section}>
                        <h4 className={styles.sectionTitle}>
                            Atlananlar
                            <span className={`${styles.sectionCount} num`}>
                                {bundlesSkipped.length + plansSkipped.length}
                            </span>
                        </h4>
                        <p className={styles.emptyNote}>
                            Zaten var olan satırlara dokunulmadı; <strong>mevcut fiyatlar korundu.</strong>
                        </p>
                        {bundlesSkipped.length > 0 && <BundleList items={bundlesSkipped} />}
                        {plansSkipped.length > 0 && <BundleList items={plansSkipped} />}
                    </section>
                )}
            </>
        );
    };

    const renderPicker = () => {
        const selected = SYNC_CATALOGS.find((c) => c.value === catalog);
        return (
            <div className={styles.body}>
                <div className={styles.field}>
                    <label htmlFor="sync-catalog">Katalog dilimi</label>
                    <select
                        id="sync-catalog"
                        value={catalog}
                        onChange={(e) => setCatalog(e.target.value)}
                        disabled={isPreviewing}
                    >
                        {SYNC_CATALOGS.map((c) => (
                            <option key={c.value} value={c.value} disabled={c.disabled}>
                                {c.label}{c.disabled ? ' — desteklenmiyor' : ''}
                            </option>
                        ))}
                    </select>
                    {selected?.note && <span className={styles.hint}>{selected.note}</span>}
                </div>

                <div className={styles.infoBox}>
                    Sync <strong>yalnızca ekleme</strong> yapar: hiçbir satır silinmez, hiçbir fiyat
                    güncellenmez. Sağlayıcı yanıtları 1 hafta cache'lendiği için fiyat değişimleri
                    gecikmeli görünebilir.
                </div>

                {error && <p className={styles.errorText}>{error}</p>}

                <div className={styles.actions}>
                    <button type="button" className="ghost" onClick={onClose} disabled={isPreviewing}>
                        Vazgeç
                    </button>
                    <button
                        type="button"
                        onClick={() => handlePreview(catalog)}
                        disabled={isPreviewing || selected?.disabled}
                    >
                        {isPreviewing ? 'Karşılaştırılıyor…' : 'Önizle (dry run)'}
                    </button>
                </div>
            </div>
        );
    };

    const renderPreview = () => (
        <div className={styles.body}>
            {renderReport(plan, false)}

            <div className={styles.warning}>
                Eklenen paketler <strong>yayına kapalı</strong> doğar (<code>isActive=false</code>).
                Her plana fiyat girilip bundle aktifleştirilene kadar uygulamada görünmez.
            </div>

            {error && <p className={styles.errorText}>{error}</p>}

            <div className={styles.actions}>
                <button type="button" className="ghost" onClick={() => setPlan(null)} disabled={isApplying}>
                    Geri
                </button>
                <button type="button" onClick={handleApply} disabled={isApplying}>
                    {isApplying ? 'Uygulanıyor…' : 'Onayla ve uygula'}
                </button>
            </div>
        </div>
    );

    const renderApplied = () => {
        const steps = applied.nextSteps || [];
        return (
            <div className={styles.body}>
                <div className={styles.successBanner}>
                    <strong>{applied.catalog}</strong> katalogu senkronize edildi. Eklenen satırlar
                    yayına kapalı (<code>isActive=false</code>) — uygulamada henüz görünmüyor.
                </div>

                {renderReport(applied, true)}

                {steps.length > 0 && (
                    <section className={styles.section}>
                        <h4 className={styles.sectionTitle}>Sıradaki adımlar</h4>
                        <ol className={styles.stepList}>
                            {steps.map((step, index) => (
                                <li key={index} className={styles.stepItem}>{step}</li>
                            ))}
                        </ol>
                    </section>
                )}

                <div className={styles.actions}>
                    <button type="button" onClick={onClose}>Kapat</button>
                </div>
            </div>
        );
    };

    let title = 'Katalogu senkronize et';
    if (applied) title = `Uygulandı: ${applied.catalog}`;
    else if (plan) title = `Önizleme (dry run): ${plan.catalog}`;

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={title}>
            {isPreviewing ? (
                <div className={styles.loadingBox}>
                    <LoadingSpinner />
                    <p>Sağlayıcı katalogu ile karşılaştırılıyor…</p>
                </div>
            ) : applied ? (
                renderApplied()
            ) : plan ? (
                renderPreview()
            ) : (
                renderPicker()
            )}
        </Modal>
    );
};

export default CatalogSyncModal;
